// src/pages/Transactions.jsx
import { useState, useMemo } from "react";
import { Search, AlertCircle, CheckCircle } from "lucide-react";
import { formatCurrency, formatDate } from "../lib/utils";
import { useTransactions } from "../hooks/useFinancialData";
import { db } from "../lib/firebase";
import { doc, updateDoc } from "firebase/firestore";
import { cn } from "../lib/utils";

const USER_ID = "gavin";

const DEMO_TRANSACTIONS = [
  { id: "1", name: "Reynolds Construction", amount: 1082, category: "Primary income", account: "HHCU Checking", date: new Date(2025, 4, 4), needsReview: false, type: "income" },
  { id: "2", name: "Raising Cane's", amount: -12.40, category: "Eating out", account: "Robinhood CC", date: new Date(2025, 4, 5), needsReview: false, type: "spend" },
  { id: "3", name: "OpenAI", amount: -20.00, category: "Subscriptions", account: "Robinhood CC", date: new Date(2025, 4, 5), needsReview: false, type: "spend" },
  { id: "4", name: "FedEx", amount: 0, category: null, account: "HHCU Checking", date: new Date(2025, 4, 4), needsReview: true, type: "spend" },
  { id: "5", name: "Champions Pointe Golf", amount: -85, category: "Golf rounds", account: "Robinhood CC", date: new Date(2025, 4, 4), needsReview: false, type: "spend" },
  { id: "6", name: "McDonald's", amount: -9.41, category: "Eating out", account: "Robinhood CC", date: new Date(2025, 4, 3), needsReview: false, type: "spend" },
  { id: "7", name: "Transfer to Venmo", amount: -75, category: "Sports betting", account: "HHCU Checking", date: new Date(2025, 4, 3), needsReview: false, type: "spend" },
  { id: "8", name: "Walmart", amount: -26.85, category: "Groceries", account: "Robinhood CC", date: new Date(2025, 4, 3), needsReview: false, type: "spend" },
  { id: "9", name: "Kroger", amount: -44.12, category: "Groceries", account: "Robinhood CC", date: new Date(2025, 4, 2), needsReview: false, type: "spend" },
  { id: "10", name: "Murphy USA", amount: -54.20, category: "Gas / fuel", account: "USAA CC", date: new Date(2025, 4, 2), needsReview: false, type: "spend" },
  { id: "11", name: "Spotify", amount: -11.99, category: "Subscriptions", account: "Robinhood CC", date: new Date(2025, 4, 1), needsReview: false, type: "spend" },
  { id: "12", name: "Amazon", amount: -34.99, category: null, account: "Robinhood CC", date: new Date(2025, 4, 1), needsReview: true, type: "spend" },
];

const FILTERS = ["All", "REVIEW", "Income", "Eating out", "Groceries", "Subscriptions", "Golf"];

const CATEGORY_COLORS = {
  "Primary income": "bg-accent-green-muted text-accent-green",
  "Eating out": "bg-[rgba(216,90,48,0.12)] text-[#D85A30]",
  Groceries: "bg-accent-green-muted text-accent-green-dim",
  Subscriptions: "bg-accent-blue-muted text-accent-blue",
  "Sports betting": "bg-accent-purple-muted text-accent-purple",
  "Gas / fuel": "bg-accent-amber-muted text-accent-amber",
  "Golf rounds": "bg-background-elevated text-text-secondary",
};

function groupByDate(transactions) {
  const groups = {};
  transactions.forEach((tx) => {
    const date = tx.date?.toDate?.() || tx.date;
    const key = formatDate(date, "MMM d, yyyy");
    if (!groups[key]) groups[key] = [];
    groups[key].push(tx);
  });
  return groups;
}

function TxRow({ tx, onResolve }) {
  const date = tx.date?.toDate?.() || tx.date;
  const isIncome = tx.amount > 0;
  const initials = tx.name?.slice(0, 2).toUpperCase() || "?";

  return (
    <div className={cn(
      "flex items-center gap-3 px-4 py-3 border-b border-border last:border-0",
      tx.needsReview && "bg-accent-amber-muted/30"
    )}>
      <div className={cn(
        "w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-semibold font-mono flex-shrink-0",
        isIncome ? "bg-accent-green-muted text-accent-green" : "bg-background-elevated text-text-secondary"
      )}>
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-text-primary truncate">{tx.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {tx.needsReview ? (
            <span className="pill pill-amber text-[10px]">REVIEW</span>
          ) : (
            <span className={cn(
              "text-[11px] px-1.5 py-0.5 rounded-md",
              CATEGORY_COLORS[tx.category] || "text-text-tertiary"
            )}>
              {tx.category || "Uncategorized"}
            </span>
          )}
          <span className="text-[11px] text-text-tertiary">· {tx.account}</span>
        </div>
        {tx.needsReview && (
          <button
            onClick={() => onResolve(tx.id)}
            className="mt-1.5 text-[11px] text-accent-green flex items-center gap-1"
          >
            <CheckCircle size={11} /> Mark resolved
          </button>
        )}
      </div>
      <span className={cn(
        "text-[14px] font-semibold font-tabular flex-shrink-0",
        isIncome ? "text-accent-green" : "text-text-primary"
      )}>
        {isIncome ? "+" : ""}{formatCurrency(tx.amount)}
      </span>
    </div>
  );
}

export function Transactions() {
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const { transactions, loading } = useTransactions({ limit: 200 });

  const displayTx = transactions.length ? transactions : DEMO_TRANSACTIONS;

  const reviewCount = displayTx.filter((t) => t.needsReview).length;

  const filtered = useMemo(() => {
    return displayTx.filter((tx) => {
      const matchSearch = !search || tx.name?.toLowerCase().includes(search.toLowerCase()) || tx.category?.toLowerCase().includes(search.toLowerCase());
      const matchFilter =
        activeFilter === "All" ||
        (activeFilter === "REVIEW" && tx.needsReview) ||
        (activeFilter === "Income" && tx.amount > 0) ||
        tx.category?.toLowerCase().includes(activeFilter.toLowerCase());
      return matchSearch && matchFilter;
    });
  }, [displayTx, search, activeFilter]);

  const grouped = useMemo(() => groupByDate(filtered), [filtered]);

  async function handleResolve(txId) {
    try {
      await updateDoc(doc(db, "users", USER_ID, "transactions", txId), {
        needsReview: false,
      });
    } catch (e) {
      console.log("Demo mode — resolve would update Firestore in production");
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border">
        <div className="px-4 py-3 flex justify-between items-center">
          <h1 className="font-display text-lg font-bold text-text-primary">Transactions</h1>
          {reviewCount > 0 && (
            <span className="pill pill-amber">
              <AlertCircle size={11} />
              {reviewCount} to review
            </span>
          )}
        </div>

        {/* Search */}
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2 bg-background-elevated border border-border rounded-xl px-3 py-2">
            <Search size={14} className="text-text-tertiary flex-shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search transactions..."
              className="bg-transparent text-[13px] text-text-primary placeholder:text-text-tertiary focus:outline-none flex-1"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto no-scrollbar">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className={cn(
                "flex-shrink-0 px-3 py-1 rounded-full text-[11px] font-medium transition-all border",
                activeFilter === f
                  ? f === "REVIEW"
                    ? "bg-accent-amber-muted text-accent-amber border-accent-amber/20"
                    : "bg-accent-green-muted text-accent-green border-accent-green/20"
                  : "text-text-tertiary border-border bg-transparent"
              )}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Transaction list */}
      <div className="flex-1 overflow-y-auto pb-6">
        {Object.entries(grouped).map(([dateLabel, txs]) => (
          <div key={dateLabel}>
            <div className="px-4 py-2 bg-background">
              <span className="text-[11px] font-mono text-text-tertiary uppercase tracking-wider">
                {dateLabel}
              </span>
            </div>
            <div className="bg-background-secondary border-y border-border">
              {txs.map((tx) => (
                <TxRow key={tx.id} tx={tx} onResolve={handleResolve} />
              ))}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-text-tertiary">
            <Search size={32} className="mb-3 opacity-40" />
            <p className="text-sm">No transactions found</p>
          </div>
        )}
      </div>
    </div>
  );
}
